import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { VendorService } from '../vendor.service';
import { Vendor } from '../vendor';
import { getDomainOptions } from '@app/_constants/task-code-registry';

@Component({
  selector: 'app-vendor-edit',
  templateUrl: './vendor-edit.component.html',
  styleUrls: ['./vendor-edit.component.scss'],
  providers: [MessageService],
  standalone: false
})
export class VendorEditComponent implements OnInit {

  form: FormGroup;
  vendorId: number | null = null;
  loading = false;
  saving = false;
  isNew = false;

  regionOptions = getDomainOptions('REGION');
  vendorTypeOptions = getDomainOptions('VENDOR_TYPE');
  paymentOptions = getDomainOptions('PAYMENT');

  constructor(
    private fb: FormBuilder,
    private vendorService: VendorService,
    private route: ActivatedRoute,
    public router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.vendorId = Number(id);
      this.loadVendor(this.vendorId);
    } else {
      this.isNew = true;
    }
  }

  buildForm(): void {
    this.form = this.fb.group({
      // Identity
      name: ['', Validators.required],
      legalName: [''],
      primaryContactName: [''],
      primaryContactEmail: ['', Validators.email],
      primaryContactPhone: [''],
      // Address
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      stateOrProvince: [''],
      postalCode: [''],
      country: ['US'],
      // Tax + Compliance
      taxId: [''],
      w9OnFile: [false],
      is1099Required: [true],
      // Classification
      region: [30],
      vendorType: [130],
      // Payment
      paymentMethod: [20],
      achRoutingNumber: [''],
      achAccountNumberEncrypted: [''],
      paymentTermsDays: [30],
      // External Refs
      qbVendorRef: [''],
      defaultRateCardId: [null],
      // Status
      isActive: [true],
      deactivatedDate: [null],
      deactivationReason: [''],
      // Notes
      notes: ['']
    });

    this.form.get('isActive').valueChanges.subscribe(active => {
      if (active) {
        this.form.get('deactivatedDate').setValue(null);
        this.form.get('deactivationReason').setValue('');
      }
    });
  }

  loadVendor(id: number): void {
    this.loading = true;
    this.vendorService.findById(id).subscribe({
      next: (res) => {
        if (res && res.data) {
          const v: Vendor = res.data;
          this.form.patchValue(v);
        }
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load vendor' });
        this.loading = false;
      }
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const vendor: Vendor = { ...this.form.value, vendorId: this.vendorId };

    this.vendorService.save(vendor).subscribe({
      next: (res) => {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Vendor saved successfully' });
        this.saving = false;
        if (this.isNew && res.data && res.data.vendorId) {
          this.router.navigate(['/vendors', res.data.vendorId]);
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save vendor' });
        this.saving = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/vendors']);
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return ctrl && ctrl.invalid && ctrl.touched;
  }

  get isInactive(): boolean {
    return !this.form.get('isActive').value;
  }

  // Mask taxId: show only last 4 chars
  get maskedTaxId(): string {
    const val = this.form.get('taxId').value || '';
    if (val.length <= 4) { return val; }
    return '***-**-' + val.slice(-4);
  }
}
